import {
  wearsSteampack,
  CHARACTER,
  DAMAGE,
  INPUT_DT,
  INVENTORY,
  ITEMS,
  NET,
  PROTOCOL_VERSION,
  SHIP_GRID,
  SNAPSHOT,
  SnapshotClient,
  T,
  applyShipControl,
  encodeInputs,
  shutdownWarning,
  isIncomer,
  memberId,
  trackAt,
  outageState,
  atHelmOrTurret,
  count,
  decodeShapes,
  decodeTiles,
  cellTile,
  setCell,
  editTargetCenter,
  editTargetTile,
  grappleHit,
  hookPoint,
  surfaceUp,
  leapDirection,
  upVector,
  LEAP,
  vAdd,
  vScale,
  flightReadout,
  isContainer,
  furnaceCapacity,
  lerpAngle,
  makeGrid,
  pieceOf,
  playerWorldCenter,
  shipCenter,
  shipStats,
  shipSurroundings,
  shipToWorld,
  slotsUsed,
  stepPlayer,
  placeRocks,
  carryRiders,
  rockOrigin,
  rockUnder,
  stepKnocks,
  tileNear,
  turretHostOf,
  turretOf,
  hostTurrets,
  vDist,
  vLen,
  vLerp,
  wrapAngle,
  plantAt,
  type AsteroidBody,
  type ClientMessage,
  type BandState,
  type CreatureState,
  type DropState,
  type EditTarget,
  type Look,
  type PlayerInput,
  type PlayerState,
  type ServerMessage,
  type ShipBody,
  type ShipState,
  type AreaRocks,
  type ShipTiles,
  type Sim,
  type Snapshot,
  type SphereConfig,
  type Vec2,
  type AsteroidDef,
} from "@dyson/shared";
import { screenH, screenW } from "@massivewebgames/screen";
import { Sfx } from "../audio/Sfx";
import { Input, type InputAxes } from "../input/Input";
import type { Action } from "../input/bindings";
import { AreaLinks } from "../net/AreaLinks";
import { PasswordRequired, keepNodeToken, startSession, type Session } from "../net/session";
import { loadLayout } from "../net/layout";
import type { PathView } from "../render/PathLine";
import { Renderer } from "../render/Renderer";
import type { ShipPose } from "../render/ShipView";
import { CraftingPanel } from "../ui/Crafting";
import { Hud } from "../ui/Hud";
import { Indicators, type Indicator } from "../ui/Indicators";
import { Minimap, type MapShip } from "../ui/Minimap";
import { SettingsPanel, loadSettings, type Settings } from "../ui/Settings";
import { SignEditor } from "../ui/SignEditor";
import { Chat } from "../ui/Chat";
import { Building } from "./Building";
import { GameAudio } from "./GameAudio";
import { ClientWorld } from "./ClientWorld";
import { Snapshots, type Interpolated } from "./Snapshots";
import { characterMode, statusText, touchSituation, touchWording } from "./statusText";
import type { ShipPhysics } from "@dyson/shared/physics";

/**
 * Input steps sent per message: three 60 Hz steps, 20 messages a second (a
 * press or a click goes at once, see fixedStep). Not more: the server plays
 * one step a substep and carries riders along with their rock every
 * substep whether a step came or not, so steps must keep arriving at that
 * rate or its carrying and ours (once per step replayed) come apart.
 */
const INPUT_BATCH = 3;
/**
 * How far the braced line reaches, in blocks: far enough for a full brace
 * (LEAP.maxSpeed over CHARACTER.airDrag: about 870), which is what it's for.
 */
const PATH_REACH = 900;
/** Something handed between areas is kept this long (ms) for the area it went to to send it. */
const TRANSIT_MS = 1500;
/** Two seconds of unacknowledged inputs at 60 Hz. */
const MAX_PENDING = 120 * 2;
/** How long a notice (e.g. "your pickaxe broke") stays on screen, ms. */
const NOTICE_MS = 4000;
/** Reconnecting after a dropped connection: the first try after this long (ms), doubling up to the most. */
const RECONNECT_MS = [1000, 15_000] as const;
/** Pirates this close make the music tense, and show at the screen's edge. */
const DANGER_RANGE = 600;

/**
 * Client orchestration: networking, prediction/reconciliation for the local
 * character (and the ship they are flying), and feeding the renderer and HUD.
 * Digging, building and the quick-access bar live in Building; interpolation
 * of everyone else in Snapshots; sounds in GameAudio.
 */
export class Game {
  /** Something stopped us joining (`password`: the server wants its shared password). */
  onError: (message: string, password?: boolean) => void = () => {};
  /** A session started (or renewed): whether logins are available, and whether we're signed in. */
  onSession: (session: Session) => void = () => {};

  private readonly renderer: Renderer;
  private readonly hud = new Hud();
  private readonly minimap = new Minimap();
  private readonly crafting = new CraftingPanel();
  private readonly indicators = new Indicators();
  private readonly signEditor = new SignEditor();
  /** Chat (Enter), and commands ("/"). Not while the sign editor or another panel has the keyboard. */
  private readonly chat = new Chat((text) => this.send({ t: "chat", text }), () => this.signEditor.isOpen);
  private readonly settingsPanel: SettingsPanel;
  private settings: Settings = loadSettings();
  private readonly sfx = new Sfx();
  private readonly audio: GameAudio;
  private readonly input: Input;
  private links?: AreaLinks;
  private session?: Session;
  private world?: ClientWorld;
  /** Which area each player and ship we have came from (each area's snapshots speak only for its own). */
  private readonly playerArea = new Map<string, string>();
  private readonly shipArea = new Map<string, string>();
  /** Ships and players handed from one area to another: kept until (ms) the area they went to sends them. */
  private readonly transit = new Map<string, number>();
  private readonly snapshotsByArea = new Map<string, Snapshots>();
  /** Each area's replica of what it's shown us (see SNAPSHOT), kept up to date by its snapshots. */
  private readonly syncByArea = new Map<string, SnapshotClient<Snapshot>>();
  private readonly creaturesByArea = new Map<string, CreatureState[]>();
  /** Calm bands of wildlife, with their scripts (see bands.ts): drawn from the keys at the rock clock. */
  private readonly bandsByArea = new Map<string, BandState[]>();
  private bands: BandState[] = [];
  private readonly firesByArea = new Map<string, [string, number][]>();
  private readonly riftsByArea = new Map<string, [number, number, number][]>();
  /** Inputs waiting to go (see flushInputs), and the last one sent. */
  private outBatch: PlayerInput[] = [];
  private lastSent?: PlayerInput;
  private readonly building: Building;
  private lastAxes: InputAxes = { moveX: 0, moveY: 0, turn: 0, interact: false };

  private sim?: Sim;
  /** Local copy of the ship physics, used to predict the ship we are flying. */
  private physics?: ShipPhysics;
  /**
   * Rapier is 2.4 MB of WebAssembly that only the ship we fly needs, so it
   * loads behind the join screen rather than before it. Until it's here the
   * ship we're flying is drawn as the server says, not predicted.
   */
  private readonly physicsReady = import("@dyson/shared/physics").then(async (m) => {
    await m.initPhysics();
    return m;
  });
  private readonly asteroidById = new Map<string, AsteroidBody>();
  private myPlayerId?: string;
  private myName = "";
  private myLook?: Look;
  /** Players on the server, from the last snapshot (we only see the ones near us). */
  private online = 0;

  /** Latest authoritative state of the players near us (including our own). Snapshots only carry changes; this is the whole picture. */
  private readonly players = new Map<string, PlayerState>();
  /** Our locally predicted character, re-derived from the server state each snapshot. */
  private predicted?: PlayerState;
  /** While piloting, the ship we fly is predicted too. */
  private predictedShip?: ShipState;
  /** Prediction state one input step earlier, so rendering can blend between steps. */
  private prevPredicted?: PlayerState;
  /** Which drifting rock prevPredicted rode, and where on it (see rideOf). */
  private prevRide?: { id: string; rel: Vec2 };
  private prevPredictedShip?: ShipState;
  /**
   * When a snapshot corrects our predicted ship, the jump is hidden by drawing
   * it offset from where it really is and easing that offset away.
   */
  private shipCorrection = { x: 0, y: 0, angle: 0 };
  /**
   * The same for our character out in the world, and for the rock clock
   * (seconds): each snapshot sets the clock afresh (see reconcile), a step or
   * so either way, and anyone carried by a drifting rock jumps with it. Both
   * are drawn offset by the jump and eased back together, so the jump never
   * shows, and someone beside a rock stays beside it all the while.
   */
  private selfCorrection = { x: 0, y: 0 };
  private rockCorrection = 0;
  /**
   * Riding a drifting rock we're drawn on the rock as it's drawn, and only
   * our place on it is eased (this): so the rock, and whatever's under the
   * mouse, holds still on screen however the clock is corrected.
   */
  private rideCorrection = { x: 0, y: 0 };
  private readonly pending: PlayerInput[] = [];
  private seq = 0;
  /**
   * The clock drifting rocks follow (see placeRocks), as the server will have
   * it when it plays our latest input: a snapshot's time, plus the inputs it
   * hasn't played yet.
   */
  private rockTime = 0;
  /** The clock the rocks were drawn at last frame (see drawnRockTime): where the mouse was over them, through that frame's camera. */
  private lastDrawnRockTime = 0;
  private frameMs = 16;

  /** Things in view the snapshots told us about: whales, flaring suns, brush fires. */
  private creatures: CreatureState[] = [];
  /** Loose items lying about in view (see DROPS). */
  private drops: DropState[] = [];
  private readonly dropsByArea = new Map<string, DropState[]>();
  private flares = new Map<string, number>();
  /** Suns in an outage (seconds since each began, see outageState) and rifts opening, from the last snapshot. */
  private shutdown: number | null = null;
  private rifts: [number, number, number][] = [];
  private groundFires: [string, number][] = [];
  private groundFireSet = new Set<string>();
  /** What each sign says, by "kind:id:x:y", as the server last told us; and ones asked about. */
  private readonly signs = new Map<string, string>();
  private readonly signsAsked = new Set<string>();
  /** A sign we're about to write on (waiting for what it says now). */
  private signToEdit: EditTarget | null = null;
  /** The ship we were last aboard, to point back to it once we've stepped off. */
  private lastShipId: string | null = null;

  /** Turn the camera with the ship you are aboard (V toggles). */
  private cameraTurns = this.settings.cameraTurns;
  /** Photo mode (P): no HUD, and the camera pans free of the character. */
  private photo = false;
  private photoPan = { x: 0, y: 0 };
  private accumulator = 0;
  private lastFrame = performance.now();
  private pingMs = 0;
  private status = "";
  private notice: { text: string; until: number } | null = null;
  private pingTimer?: ReturnType<typeof setInterval>;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private reconnects = 0;

  /** `base`: the game server's http(s) address; `wsBase`: the same as ws(s). */
  constructor(
    canvas: HTMLCanvasElement,
    private readonly base: string,
    private readonly wsBase: string,
  ) {
    this.renderer = new Renderer(canvas);
    this.audio = new GameAudio(this.sfx, this.renderer);
    this.input = new Input(canvas, document.getElementById("hud")!);
    this.settingsPanel = new SettingsPanel(this.settings);
    this.settingsPanel.onChange = (s) => this.applySettings(s);
    this.applySettings(this.settings);
    this.building = new Building({
      sim: () => (this.sim ? this.predSim() : undefined),
      me: () => this.predicted,
      players: () => this.players.values(),
      mouse: () => this.mouseOnRocks(this.renderer.screenToWorld(this.input.mouseX, this.input.mouseY)),
      drawnOrigin: (def) => this.drawnRockOrigin(def),
      leftDown: () => this.input.leftDown,
      rightDown: () => this.input.rightDown,
      send: (msg) => this.send(msg),
      grapple: () => {
        if (this.predicted?.grappleOut == null) this.audio.play("grapple");
        this.input.grapple();
      },
      interact: () => this.input.interact(),
      say: (text) => this.say(text),
      swing: () => this.myPlayerId && this.renderer.swing(this.myPlayerId),
      openChest: (target) => this.send({ t: "chest", target }),
      editSign: (target) => {
        // Ask what it says now, and open the editor with that when it comes.
        this.signToEdit = target;
        this.send({ t: "sign", target });
      },
      groundFires: () => this.groundFireSet,
    });
    this.input.onKey = (e) => this.onKey(e);
    this.input.onPadAction = (a) => this.doAction(a);
    this.input.onPadSlot = (step) => {
      this.building.selected = (this.building.selected + step + this.building.quickbar.length) % this.building.quickbar.length;
      this.sfx.play("select");
    };
    this.input.onPointer = () => this.building.paint();
    // The touch controls: their buttons are actions, two fingers zoom, and the help and status describe them while they're up.
    this.input.touch.onZoom = (f) => this.renderer.zoomBy(f);
    this.input.touch.onChat = () => this.chat.open();
    this.input.touch.onHelp = () => this.hud.toggleHelp();
    this.input.touch.onMap = () => this.minimap.setBig(true);
    this.input.touch.onEnabled = (on) => this.hud.setTouch(on);
    this.crafting.onPick = (item) => {
      this.building.assign(this.building.selected, item);
      this.sfx.play("select");
    };
    this.crafting.onCraft = (recipe) => {
      this.send({ t: "craft", recipe });
      this.sfx.play("placeWood");
    };
    this.crafting.onReturn = (i) => this.building.assign(i, null);
    this.crafting.onEquip = (item) => {
      this.send({ t: "equip", item });
      this.sfx.play("select");
    };
    this.crafting.onUnequip = (slot) => {
      this.send({ t: "unequip", slot });
      this.sfx.play("select");
    };
    this.crafting.onChestMove = (item, count) => {
      const target = this.crafting.chest?.target;
      if (target) this.send({ t: "chest", target, move: { item, count } });
    };
    this.hud.onSelect = (i) => {
      this.building.selected = i;
      this.sfx.play("select");
    };
    this.hud.onAssign = (i, item) => this.building.assign(i, item);
    this.hud.onClear = (i) => this.building.assign(i, null);
    this.hud.onSwap = (a, b) => this.building.swap(a, b);
    this.minimap.onTeleport = (at) => {
      this.send({ t: "teleport", x: at.x, y: at.y });
      this.sfx.play("launch");
    };
    requestAnimationFrame((t) => this.frame(t));
  }

  join(name: string, look?: Look): void {
    this.myName = name;
    this.myLook = look;
    this.status = "Connecting...";
    void this.connectAll();
  }

  private scheduleReconnect(reason: string): void {
    if (this.reconnectTimer) return;
    const wait = Math.min(RECONNECT_MS[1], RECONNECT_MS[0] * 2 ** this.reconnects);
    this.reconnects++;
    this.status = `Connection lost (${reason}): reconnecting in ${Math.round(wait / 1000)} s...`;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      void this.connectAll();
    }, wait);
  }

  /** Settings changed (or loaded): keys, sound, bloom, camera. */
  private applySettings(s: Settings): void {
    this.settings = s;
    this.input.setKeys(s.keys);
    this.hud.setKeys(s.keys);
    this.input.touch.setPreference(s.touch);
    this.hud.setTouch(this.input.touch.enabled);
    this.sfx.setVolumes(s.volume);
    this.renderer.setBloom(s.bloom);
  }

  // ------------------------------------------------------------------ input

  private onKey(e: KeyboardEvent): void {
    if (!this.sim || this.signEditor.isOpen) return;
    if (/^Digit[1-9]$/.test(e.code)) {
      const i = Number(e.code.slice(5)) - 1;
      // Over an item in the inventory, the number puts it in that slot; otherwise it picks the slot.
      if (this.crafting.hovered) this.building.assign(i, this.crafting.hovered);
      else this.building.selected = i;
      this.sfx.play("select");
      return;
    }
    if (e.code === "Escape") {
      this.crafting.close();
      this.settingsPanel.close();
      this.setPhoto(false);
      return;
    }
    for (const action of this.input.actionsOf(e.code)) this.doAction(action);
  }

  /** A key (or pad button) for `action` was pressed: the things that happen outside the simulation step. */
  private doAction(action: Action): void {
    const me = this.predicted;
    if (this.settingsPanel.isOpen && action !== "settings") return;
    if (this.photo && action !== "photo") return;
    switch (action) {
      case "inventory":
        this.crafting.toggle();
        break;
      case "mute":
        this.say(this.sfx.toggleMute() ? "Sound off (M)" : "Sound on");
        break;
      case "camera":
        this.cameraTurns = !this.cameraTurns;
        break;
      case "throw":
        // Nothing in hand, nothing to throw; holding on, the rock takes the push (see THROW).
        if (me?.holding) this.input.throwItem();
        else this.say("Nothing in your hand to throw: pick a quick-access slot with something in it.");
        break;
      case "respawn":
        if (!this.input.ctrl) this.send({ t: "respawn" });
        break;
      case "undo":
        if (this.input.ctrl) {
          this.send({ t: "undo" });
          this.audio.play("undo");
        }
        break;
      case "rotate": {
        const what = this.building.rotate();
        if (what) this.say(what);
        break;
      }
      case "eat": {
        // The plainest food first: save the rare healers for when they're needed.
        const food = (["skyfish", "spores"] as const).find((f) => count(this.building.inventory, f) > 0);
        if (food) {
          this.send({ t: "eat", item: food });
          this.audio.play("eat");
        } else this.say("Nothing to eat: catch skyfish, or harvest glow fungi for their spores");
        break;
      }
      case "photo":
        this.setPhoto(!this.photo);
        break;
      case "settings":
        this.settingsPanel.toggle();
        break;
    }
  }

  private setPhoto(on: boolean): void {
    this.photo = on;
    this.photoPan = { x: 0, y: 0 };
    this.input.freeze = on;
  }

  /** Show a line in the status bar for a few seconds. */
  private say(text: string): void {
    this.notice = { text, until: performance.now() + NOTICE_MS };
  }

  // ---------------------------------------------------------------- network

  /** A message from one of our area sockets (`from`): the areas and what's in them, the world's tiles, and the UI's answers. */
  private handle(m: ServerMessage, from: string): void {
    switch (m.t) {
      // ---- areas: joining, watching, and what crosses between them
      case "welcome":
        this.onWelcome(m, from);
        break;
      case "viewing":
        if (this.sim) this.takeArea(from, m.players, m.ships, m.shipTiles, m.rocks);
        break;
      case "owned":
        // Our character crossed into this area: play through it, and send it what the last one hadn't acknowledged.
        this.links?.owned(from);
        for (const input of this.pending) this.outBatch.push(input);
        this.flushInputs(true);
        break;
      case "handoff": {
        // Keep drawing what crossed until the area it went to sends it.
        const until = performance.now() + TRANSIT_MS;
        for (const id of [...m.ships, ...m.players]) this.transit.set(id, until);
        if (from === this.links?.ownerKey && this.myPlayerId && m.players.includes(this.myPlayerId)) this.links.handedOff(m.to);
        break;
      }
      case "snapshot":
        this.onSnapshot(m.data, from);
        break;
      case "ship":
        this.addShip(m.state, m);
        this.shipArea.set(m.state.id, from);
        this.transit.delete(m.state.id);
        break;
      case "left":
        if (this.playerArea.get(m.playerId) !== from) break;
        this.players.delete(m.playerId);
        this.playerArea.delete(m.playerId);
        break;
      // ---- the world: rocks, tiles and plants
      case "rock":
        this.world?.addRock(m.def);
        this.minimap.markDirty();
        break;
      case "dead":
        this.world?.kill(m.id);
        this.minimap.markDirty();
        break;
      case "vine":
        this.world?.setVine(m.id, m.cut);
        break;
      case "tile":
        if (m.kind === "ship") this.onShipTile(m.id, m.index, m.tile);
        else this.onAsteroidTile(m.id, m.index, m.tile);
        break;
      case "plant":
        this.onPlant(m.id, m.changes, m.felled);
        break;
      // ---- things to hear, see and read
      case "fx":
        this.onFx(m);
        break;
      case "chest":
        this.crafting.showChest(m.target, m.items);
        break;
      case "sign":
        this.onSign(m.target, m.text);
        break;
      case "inventory":
        this.building.takeInventory(m.items, m.wear, m.gear);
        break;
      case "notice":
        this.notice = { text: m.text, until: performance.now() + NOTICE_MS };
        if (/flare/i.test(m.text)) this.audio.play("flare");
        break;
      case "pong":
        this.pingMs = performance.now() - m.sent;
        break;
      case "chat":
        this.chat.add(m.from, m.text);
        break;
      case "error":
        this.status = m.message;
        this.onError(m.message);
        break;
    }
  }

  /** Joined an area as our own (fresh, after a reconnect, or crossing into it): take what it has and play through it. */
  private onWelcome(m: Extract<ServerMessage, { t: "welcome" }>, from: string): void {
    keepNodeToken(m.token);
    if (m.token && this.session && !this.session.ticket) {
      this.session = { ...this.session, ticket: m.token };
      this.links?.setSession(this.session);
    }
    this.links?.owned(from);
    const moving = !!this.sim && this.world?.sphere.seed === m.worldSeed && this.myPlayerId === m.playerId;
    if (!moving) this.buildWorld(m.sphere);
    this.takeArea(from, m.players, m.ships, m.shipTiles, m.rocks);
    this.building.takeInventory(m.inventory, m.wear, m.gear, !moving);

    this.myPlayerId = m.playerId;
    const me = this.players.get(m.playerId);
    this.predicted = me ? structuredClone(me) : undefined;
    this.predictedShip = undefined;
    this.prevPredicted = this.prevPredictedShip = undefined;
    this.pending.length = 0;
    this.outBatch = [];
    if (moving) return;
    this.signs.clear();
    this.signsAsked.clear();
    this.status = "";
    if (this.reconnects) this.say("Reconnected.");
    this.reconnects = 0;
    this.hud.show();
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = setInterval(() => this.send({ t: "ping", sent: performance.now() }), 2000);
  }

  /**
   * What an area says is near us now (see SNAPSHOT): what came into its
   * view, what changed, and what left. Each area speaks only for its own.
   * A packet that doesn't follow on from the last means we've lost the
   * thread of that connection, so we start over.
   */
  private onSnapshot(data: Uint8Array, from: string): void {
    if (!this.sim) return;
    const sync = this.syncOf(from);
    let changes: ReturnType<typeof sync.apply>;
    try {
      changes = sync.apply(data);
    } catch (err) {
      console.warn("[snapshot]", err);
      this.links?.closeAll();
      this.scheduleReconnect("snapshot out of step");
      return;
    }
    const header = [...sync.get("header").values()][0];
    if (!header) return;
    const owner = from === this.links?.ownerKey;
    if (owner) this.online = header.online;
    const now = performance.now();
    const inTransit = (id: string) => (this.transit.get(id) ?? 0) > now;

    for (const p of changes.players.created) {
      this.playerArea.set(p.id, from);
      this.transit.delete(p.id);
    }
    for (const p of changes.players.created) this.players.set(p.id, p);
    for (const p of changes.players.updated) this.players.set(p.id, p);
    // One that left this area's view is gone, unless it's crossing to another area, which will send it (see "handoff").
    const players = sync.get("players");
    for (const [id, area] of [...this.playerArea]) {
      if (area !== from || players.has(id) || inTransit(id)) continue;
      this.players.delete(id);
      this.playerArea.delete(id);
    }
    for (const s of changes.ships.created) {
      this.shipArea.set(s.id, from);
      this.transit.delete(s.id);
    }
    for (const list of [changes.ships.created, changes.ships.updated]) {
      for (const s of list) {
        const body = this.sim.ships.get(s.id);
        if (body) body.state = s;
      }
    }
    // A ship that left this area's view is forgotten until it's sent again.
    const ships = sync.get("ships");
    for (const [id, area] of [...this.shipArea]) if (area === from && !ships.has(id) && !inTransit(id)) this.removeShip(id);
    // Knocked rocks, as the area has them now (each settles back on its own once it stops saying).
    for (const list of [changes.knocks.created, changes.knocks.updated]) {
      for (const k of list) (this.sim.knocks ??= new Map()).set(k.id, { x: k.x, y: k.y, vx: k.vx, vy: k.vy, ...(k.o ? { o: k.o } : {}) });
    }

    const me = this.myPlayerId ? this.players.get(this.myPlayerId) : undefined;
    if (me && owner && this.playerArea.get(me.id) === from) {
      // The last input of ours it played comes in the header (nobody is sent anyone else's).
      me.lastInputSeq = header.ack;
      this.reconcile(me, header.time);
    } else if (!this.predicted) this.rockTime = header.time;

    // Turrets on asteroids: where they aim and whether they've reloaded (except one we're manning, which we predict).
    for (const t of sync.get("turrets").values()) {
      const a = this.asteroidById.get(t.rock);
      if (!a) continue;
      const turret = turretOf(a, t.anchor);
      turret.cooldown = t.cooldown;
      if (this.predicted?.gunningAsteroid !== t.rock || this.predicted.gunning !== t.anchor) turret.aim = t.aim;
    }
    this.dropsByArea.set(from, [...sync.get("drops").values()]);
    this.drops = [...this.dropsByArea.values()].flat();
    this.creaturesByArea.set(from, [...sync.get("creatures").values()]);
    this.creatures = [...this.creaturesByArea.values()].flat();
    this.bandsByArea.set(from, [...sync.get("bands").values()]);
    this.bands = [...this.bandsByArea.values()].flat();
    // Flares come from the seed and the clock, so every area tells the same story; any will do.
    this.flares = new Map([...sync.get("flares").values()].map((f): [string, number] => [f.id, f.t]));
    this.shutdown = [...sync.get("outages").values()][0]?.t ?? null;
    this.riftsByArea.set(from, [...sync.get("rifts").values()].map((r): [number, number, number] => [r.x, r.y, r.t]));
    this.rifts = [...this.riftsByArea.values()].flat();
    this.firesByArea.set(from, [...sync.get("fires").values()].map((f): [string, number] => [f.rock, f.index]));
    this.groundFires = [...this.firesByArea.values()].flat();
    this.groundFireSet = new Set(this.groundFires.map(([id, i]) => `${id}:${i}`));

    const shipStates = [...ships.values()].map((s) => this.sim!.ships.get(s.id)?.state).filter((s): s is ShipState => !!s);
    const projectiles = [...sync.get("projectiles").values()];
    this.snapshotsOf(from).record(header.tick, [...players.values()], shipStates, projectiles);
    this.audio.cannons(projectiles);
  }

  /** One tile of a ship changed: `cell` is the tile and its shape (see packCell). */
  private onShipTile(id: string, index: number, cell: number): void {
    const body = this.sim?.ships.get(id);
    if (!body || index < 0 || index >= body.grid.tiles.length) return;
    const tile = cellTile(cell);
    // A removal right after a hit is the hit itself; it already made its sound.
    if (tile !== T.EMPTY || !this.audio.justHit) {
      const at = shipToWorld(body, { x: (index % body.grid.w) + 0.5, y: Math.floor(index / body.grid.w) + 0.5 });
      this.blockSound(body.grid.tiles[index]!, tile, at);
      this.swingNear(at, tile);
    }
    if (body.grid.tiles[index] === T.SIGN) this.forgetSign({ kind: "ship", id, x: index % body.grid.w, y: Math.floor(index / body.grid.w) });
    setCell(body.grid, index, cell);
    body.stats = shipStats(body.grid);
    this.physics?.syncShip(body);
    this.renderer.rebuildShip(body);
  }

  /** One tile of an asteroid changed (kept for when the asteroid loads, if it hasn't). */
  private onAsteroidTile(id: string, index: number, cell: number): void {
    const a = this.asteroidById.get(id);
    if (!a || index < 0 || index >= a.grid.tiles.length) {
      this.world?.edit(id, index, cell);
      return;
    }
    const at = { x: a.def.ox + (index % a.grid.w) + 0.5, y: a.def.oy + Math.floor(index / a.grid.w) + 0.5 };
    const tile = cellTile(cell);
    this.blockSound(a.grid.tiles[index]!, tile, at);
    if (a.grid.tiles[index] === T.SIGN) this.forgetSign({ kind: "asteroid", id, x: index % a.grid.w, y: Math.floor(index / a.grid.w) });
    this.world?.edit(id, index, cell);
    this.swingNear(at, tile);
    this.physics?.setAsteroidTile(a.def.id, index % a.grid.w, Math.floor(index / a.grid.w), tile);
    this.renderer.rebuildAsteroid(a, index);
    this.minimap.markDirty();
  }

  /** A plant on asteroid `id` came down (`felled`) or grew back: all its tiles at once. */
  private onPlant(id: string, changes: [number, number][], felled: boolean): void {
    const a = this.asteroidById.get(id);
    for (const [index, tile] of changes) {
      this.world?.edit(id, index, tile);
      if (a && index >= 0 && index < a.grid.tiles.length) this.physics?.setAsteroidTile(id, index % a.grid.w, Math.floor(index / a.grid.w), tile);
    }
    // Not loaded here: kept (by the edits above) for when it is.
    if (!a) return;
    this.renderer.rebuildAsteroid(a, changes.map(([index]) => index));
    this.minimap.markDirty();
    if (!felled) return;
    // One crash for the whole plant, where it stood.
    const [i] = changes[0]!;
    const at = { x: a.def.ox + (i % a.grid.w) + 0.5, y: a.def.oy + Math.floor(i / a.grid.w) + 0.5 };
    this.audio.block(true, T.TREE, at);
    this.audio.impact(at.x, at.y, DAMAGE.impactSpeed * 2);
    const plant = plantAt(a.def, i);
    if (plant !== null) this.renderer.treeFelled(a, plant);
  }

  /** A one-off effect nearby: heard, and some of them seen. */
  private onFx(m: Extract<ServerMessage, { t: "fx" }>): void {
    if (m.kind === "hit") this.audio.hit(m.x, m.y);
    else if (m.kind === "impact") {
      this.audio.impact(m.x, m.y, m.power ?? 30);
      this.renderer.shake({ x: m.x, y: m.y }, m.power ?? 30);
    } else if (m.kind === "board") {
      // Someone came aboard the ship we're on: loud, and marked on the map.
      this.audio.board(m.x, m.y);
      this.minimap.flash({ x: m.x, y: m.y });
      this.say("Someone has come aboard!");
    } else if (m.kind === "roar") this.audio.play("whale", { x: m.x, y: m.y });
    // Something thrown is heard; the thing itself goes on as a pile in the world (see DROPS), drawn from the snapshots.
    else if (m.kind === "throw") this.audio.play("grapple", { x: m.x, y: m.y });
    else if (m.kind === "took") this.audio.play("select", { x: m.x, y: m.y });
    else this.audio.rift(m.kind, m.x, m.y);
  }

  /** What a sign says: shown in its tooltip, or put in the editor if we asked so as to write on it. */
  private onSign(target: EditTarget, text: string): void {
    this.signs.set(signKey(target), text);
    const editing = this.signToEdit;
    if (editing && signKey(editing) === signKey(target)) {
      this.signToEdit = null;
      this.signEditor.open(text, (text) => this.send({ t: "sign", target: editing, text }));
    }
  }

  private forgetSign(t: EditTarget): void {
    this.signs.delete(signKey(t));
    this.signsAsked.delete(signKey(t));
  }

  private removeShip(id: string): void {
    this.shipArea.delete(id);
    this.sim?.ships.delete(id);
    this.physics?.removeShip(id);
  }

  /** A ship arrived whole (new, rebuilt, or grown: its grid may be a new size). */
  private addShip(state: ShipState, sent: ShipTiles | undefined): void {
    if (!this.sim) return;
    const w = sent?.w ?? SHIP_GRID.w;
    const h = sent?.h ?? SHIP_GRID.h;
    const tiles = decodeTiles(sent?.tiles, w, h);
    const grid = tiles ? { w, h, tiles } : makeGrid(w, h);
    decodeShapes(sent?.shapes, grid);
    const body: ShipBody = { state, grid, stats: shipStats(grid) };
    this.sim.ships.set(state.id, body);
    this.physics?.syncShip(body);
    this.renderer.rebuildShip(body);
  }

  // ------------------------------------------------------------------ areas

  /** A fresh world for a sphere: nothing loaded yet (it loads round us as we go, see ClientWorld). */
  private buildWorld(sphere: SphereConfig): void {
    this.physics?.free();
    this.world = new ClientWorld(sphere, this.asteroidById, {
      added: (a) => {
        this.physics?.addAsteroid(a);
        this.renderer.addAsteroid(a);
      },
      removed: (id) => {
        this.physics?.removeAsteroid(id);
        this.renderer.removeAsteroid(id);
      },
    });
    const sim = (this.sim = this.world.sim);
    this.physics = undefined;
    // Once Rapier's here (if it isn't yet): the world as it is by then, rocks and ships both.
    void this.physicsReady.then(({ ShipPhysics }) => {
      if (this.sim !== sim) return;
      const physics = new ShipPhysics(sim.world, []);
      for (const a of this.asteroidById.values()) physics.addAsteroid(a);
      for (const body of sim.ships.values()) physics.syncShip(body);
      this.physics = physics;
    });
    this.renderer.buildWorld(this.sim.world, []);
    this.minimap.setWorld(this.sim.world, this.sim.asteroids, this.world.sphere);
    this.players.clear();
    this.playerArea.clear();
    this.shipArea.clear();
    this.transit.clear();
    this.snapshotsByArea.clear();
    this.syncByArea.clear();
    this.creaturesByArea.clear();
    this.dropsByArea.clear();
    this.drops = [];
    this.bandsByArea.clear();
    this.bands = [];
    this.firesByArea.clear();
    this.riftsByArea.clear();
  }

  /** What an area sent on connecting (as our own, or one we watch): its players and ships near us, and its asteroid edits. */
  private takeArea(from: string, players: PlayerState[], ships: ShipState[], shipTiles: Record<string, ShipTiles>, rocks: AreaRocks): void {
    this.world!.takeRocks(rocks);
    for (const [id, index, tile] of rocks.edits) {
      const a = this.world!.edit(id, index, tile);
      if (a) {
        this.physics?.setAsteroidTile(id, index % a.grid.w, Math.floor(index / a.grid.w), cellTile(tile));
        this.renderer.rebuildAsteroid(a, index);
      }
    }
    for (const s of ships) {
      this.addShip(s, shipTiles[s.id]);
      this.shipArea.set(s.id, from);
    }
    for (const p of players) {
      this.players.set(p.id, p);
      this.playerArea.set(p.id, from);
    }
    // The area starts over with us too: its snapshots from here on build on nothing.
    this.snapshotsOf(from).reset();
    this.syncOf(from).reset();
  }

  /** Each area's snapshots, each on its own clock (areas don't tick in step). */
  private snapshotsOf(area: string): Snapshots {
    let s = this.snapshotsByArea.get(area);
    if (!s) this.snapshotsByArea.set(area, (s = new Snapshots()));
    return s;
  }

  private syncOf(area: string): SnapshotClient<Snapshot> {
    let s = this.syncByArea.get(area);
    if (!s) this.syncByArea.set(area, (s = new SnapshotClient(SNAPSHOT)));
    return s;
  }

  /**
   * The creatures sent, and the members of calm bands where their scripts
   * have them at `time` (the rock clock, as rocks are drawn): the server
   * wrote the keys, so everyone sees them in the same place. A member sent as
   * a creature too (its band just went live) is drawn as sent.
   */
  private withBands(time: number): CreatureState[] {
    if (!this.bands.length) return this.creatures;
    const out = [...this.creatures];
    const sent = new Set(out.map((c) => c.id));
    for (const b of this.bands) {
      for (const tr of b.tracks) {
        const id = memberId(b.id, tr.m);
        if (sent.has(id)) continue;
        const k = trackAt(tr.keys, time - b.from);
        const rock = b.rocks[k.r];
        const a = rock ? this.asteroidById.get(rock) : undefined;
        if (!a) continue;
        const c: CreatureState = { id, kind: b.kind, position: { x: a.def.ox + k.at.x, y: a.def.oy + k.at.y }, velocity: { x: 0, y: 0 }, angle: k.a, health: 1, angry: false, rock, local: k.at, eating: k.e };
        if (b.kind === "grazer") c.facing = k.f;
        if (b.kind === "skyfish") {
          c.angle = 0;
          c.school = { round: k.a, turn: b.turn ?? 1, out: b.out ?? 4, n: b.size, alive: b.alive, seed: b.seed, scatter: 0 };
        }
        out.push(c);
      }
    }
    return out;
  }

  /** Everyone else, from every area we have a socket to, each blended on its own clock. */
  private interpolated(now: number): Interpolated {
    const out: Interpolated = { players: [], shipPose: new Map(), projectiles: [] };
    const seen = new Set<string>();
    for (const snaps of this.snapshotsByArea.values()) {
      const part = snaps.at(now);
      // Something just handed across is in both for a moment: draw it once.
      for (const p of part.players) if (!seen.has(p.id)) seen.add(p.id), out.players.push(p);
      for (const [id, pose] of part.shipPose) if (!out.shipPose.has(id)) out.shipPose.set(id, pose);
      out.projectiles.push(...part.projectiles);
    }
    return out;
  }

  /** To the area we play in. */
  private send(msg: ClientMessage): void {
    this.links?.send(msg);
  }

  /**
   * Inputs go INPUT_BATCH to a message (20 a second), packed (see encodeInputs), and none while we're
   * standing still with nothing pressed (see plan.md, "Quiet mode"): an area
   * where nobody moves can stop ticking.
   */
  private flushInputs(force = false): void {
    if (!this.outBatch.length || (!force && this.outBatch.length < INPUT_BATCH)) return;
    this.send({ t: "inputs", data: encodeInputs(this.outBatch) });
    this.outBatch = [];
  }

  /** Is this step nothing at all: no keys, no clicks, the aim where it was, and us standing still? */
  private idleStep(input: PlayerInput): boolean {
    const p = this.predicted;
    const still = !!p && !p.piloting && p.gunning === null && (p.grounded || !!p.frame) && Math.hypot(p.velocity.x, p.velocity.y) < 0.01;
    const last = this.lastSent;
    const same = !!last && Math.abs(last.aimX - input.aimX) < 0.25 && Math.abs(last.aimY - input.aimY) < 0.25 && last.hold === input.hold;
    return still && same && !input.moveX && !input.moveY && !input.turn && !input.interact && !input.fire && !input.action;
  }

  /** Connect (again) from scratch: a fresh session, to whichever area our character is in. */
  private async connectAll(): Promise<void> {
    try {
      this.session = await startSession(this.base);
    } catch (err) {
      if (err instanceof PasswordRequired) {
        this.status = err.message;
        this.onError(err.message, true);
        return;
      }
      this.status = `Can't reach the server: ${(err as Error).message}`;
      if (!this.myPlayerId) this.onError(this.status);
      else this.scheduleReconnect(this.status);
      return;
    }
    // The sphere's layout from the server, not made here (see loadLayout): before anything needs it.
    await loadLayout(this.base, this.session.sphere);
    this.onSession(this.session);
    this.links?.closeAll();
    const links = new AreaLinks(this.wsBase, this.session);
    this.links = links;
    links.onMessage = (m, from) => this.handle(m, from);
    links.onOwnerClosed = (reason) => {
      if (this.links !== links) return;
      this.predicted = undefined;
      if (this.pingTimer) clearInterval(this.pingTimer);
      if (!this.myPlayerId) {
        this.status = `Disconnected: ${reason}`;
        this.onError(`Could not reach the server (${reason}).`);
        return;
      }
      // Dropped mid-game: keep trying, and rejoin (aboard the same ship) when it's back.
      this.scheduleReconnect(reason);
    };
    this.status = this.reconnects ? "Reconnected, rejoining..." : "Joining...";
    links.join(this.myName, this.myLook);
  }


  /** The sim as we predict it: identical to the server's, except the ship we are flying. */
  private predSim(): Sim {
    const sim = this.sim!;
    const ps = this.predictedShip;
    const body = ps ? sim.ships.get(ps.id) : undefined;
    if (!ps || !body) return sim;
    const ships = new Map(sim.ships);
    ships.set(ps.id, { ...body, state: ps });
    return { ...sim, ships };
  }

  private predictStep(input: PlayerInput): void {
    const p = this.predicted;
    if (!p || !this.sim) return;
    if (p.piloting && p.frame) {
      if (this.predictedShip?.id !== p.frame) {
        const body = this.sim.ships.get(p.frame);
        this.predictedShip = body ? structuredClone(body.state) : undefined;
      }
    } else {
      this.predictedShip = undefined;
    }
    // Drifting rocks move on, carrying us if we're on one.
    this.rockTime += input.dt;
    carryRiders(placeRocks(this.sim, this.rockTime), [p]);
    const sim = this.predSim();
    stepPlayer(p, input, input.dt, sim);

    // Fly our ship one physics step, with every other ship where the server last put it.
    const flown = this.predictedShip && sim.ships.get(this.predictedShip.id);
    if (flown && this.physics) {
      for (const other of this.sim.ships.values()) if (other.state.id !== flown.state.id) this.physics.writePose(other);
      const { water, env } = shipSurroundings(sim, flown);
      applyShipControl(flown, input.dt, water, env);
      this.physics.writePose(flown);
      this.physics.placeAsteroids();
      this.physics.step();
      this.physics.read(flown);
    }
    stepKnocks(this.sim, input.dt);
  }

  /**
   * Standard client-side reconciliation: start from the authoritative state,
   * drop inputs the server has already applied, replay the rest.
   */
  private reconcile(server: PlayerState, time: number): void {
    let i = 0;
    while (i < this.pending.length && this.pending[i]!.seq <= server.lastInputSeq) i++;
    this.pending.splice(0, i);
    const was = this.predicted && !this.predicted.frame ? { ...this.predicted.position } : undefined;
    const wasRide = this.predicted && this.rideOf(this.predicted);
    const wasTime = this.rockTime;
    // The rocks back where they were then (no one carried: that's where the server had us), to replay from.
    this.rockTime = time;
    if (this.sim) placeRocks(this.sim, time);
    const before = this.predictedShip && { ...this.predictedShip };
    this.predicted = structuredClone(server);
    const flown = server.piloting && server.frame ? this.sim?.ships.get(server.frame) : undefined;
    this.predictedShip = flown ? structuredClone(flown.state) : undefined;
    for (const input of this.pending) this.predictStep(input);

    const after = this.predictedShip;
    if (before && after && before.id === after.id) {
      this.shipCorrection.x += before.position.x - after.position.x;
      this.shipCorrection.y += before.position.y - after.position.y;
      this.shipCorrection.angle += wrapAngle(before.angle - after.angle);
      // Big jumps (respawn, teleport, a grown grid) should just snap.
      if (Math.hypot(this.shipCorrection.x, this.shipCorrection.y) > 8) this.shipCorrection = { x: 0, y: 0, angle: 0 };
    }
    // Out in the world: our character, and the rocks round us, drawn where they were and eased onto where they are.
    this.rockCorrection += wasTime - this.rockTime;
    if (Math.abs(this.rockCorrection) > 1) this.rockCorrection = 0;
    const now = this.predicted;
    if (was && now && !now.frame) {
      this.selfCorrection.x += was.x - now.position.x;
      this.selfCorrection.y += was.y - now.position.y;
      if (Math.hypot(this.selfCorrection.x, this.selfCorrection.y) > 4) this.selfCorrection = { x: 0, y: 0 };
    } else this.selfCorrection = { x: 0, y: 0 };
    // Riding a rock, only where on it we are matters (the clock's jump moves the rock and us together).
    const nowRide = now && this.rideOf(now);
    if (wasRide && nowRide && wasRide.id === nowRide.id) {
      this.rideCorrection.x += wasRide.rel.x - nowRide.rel.x;
      this.rideCorrection.y += wasRide.rel.y - nowRide.rel.y;
      if (Math.hypot(this.rideCorrection.x, this.rideCorrection.y) > 4) this.rideCorrection = { x: 0, y: 0 };
    } else this.rideCorrection = { x: 0, y: 0 };
  }

  /** The drifting rock a character on foot rides (see rockUnder), and where on it (from its grid origin, as the sim has it now). */
  private rideOf(p: PlayerState): { id: string; rel: Vec2 } | undefined {
    const a = this.sim && rockUnder(p, this.sim);
    return a ? { id: a.def.id, rel: { x: p.position.x - a.def.ox, y: p.position.y - a.def.oy } } : undefined;
  }

  /** The clock drifting rocks are drawn at: between input steps, as we are, with the last correction easing off. */
  private drawnRockTime(): number {
    return this.rockTime - (1 - this.accumulator / INPUT_DT) * INPUT_DT + this.rockCorrection;
  }

  /** Where a rock's grid origin is drawn (a drifting one a little behind where the sim has it). */
  private drawnRockOrigin(def: AsteroidDef): Vec2 {
    return def.drift ? rockOrigin(def, this.drawnRockTime(), this.sim?.knocks?.get(def.id)) : { x: def.ox, y: def.oy };
  }

  /**
   * A point on the screen as last drawn (`w`, through that frame's camera)
   * as the sim has it now: over a drifting rock, the same spot on that rock,
   * where the sim has the rock now. A fast stream rock is drawn behind its sim
   * place by more or less each frame, and moves on a tile or so between
   * frames; without this the cursor slides about on it and a dig (a tree
   * takes seconds) keeps starting over.
   */
  private mouseOnRocks(w: Vec2): Vec2 {
    let best: { d: number; dx: number; dy: number } | null = null;
    for (const a of this.sim?.asteroids ?? []) {
      const def = a.def;
      if (!def.drift) continue;
      const o = rockOrigin(def, this.lastDrawnRockTime, this.sim?.knocks?.get(def.id));
      if (w.x < o.x || w.y < o.y || w.x > o.x + def.w || w.y > o.y + def.h) continue;
      const d = Math.hypot(w.x - o.x - def.w / 2, w.y - o.y - def.h / 2);
      if (!best || d < best.d) best = { d, dx: def.ox - o.x, dy: def.oy - o.y };
    }
    return best ? { x: w.x + best.dx, y: w.y + best.dy } : w;
  }

  /** Our own character (and piloted ship) blended between the last two input steps. */
  private blendedSelf(alpha: number): { me?: PlayerState; ship?: ShipState } {
    const cur = this.predicted;
    const prev = this.prevPredicted;
    const sc = this.selfCorrection;
    const position = (): Vec2 => {
      const at = vLerp(prev!.position, cur!.position, alpha);
      if (cur!.frame) return at;
      // Riding a drifting rock (this step and the last): at our place on it, on the rock as it's drawn.
      const ride = this.rideOf(cur!);
      const a = ride && ride.id === this.prevRide?.id ? this.asteroidById.get(ride.id) : undefined;
      if (ride && a) {
        const o = this.drawnRockOrigin(a.def);
        const rel = vLerp(this.prevRide!.rel, ride.rel, alpha);
        return { x: o.x + rel.x + this.rideCorrection.x, y: o.y + rel.y + this.rideCorrection.y };
      }
      return { x: at.x + sc.x, y: at.y + sc.y };
    };
    const me =
      cur && prev && prev.frame === cur.frame
        ? {
            ...cur,
            position: position(),
            up: lerpAngle(prev.up, cur.up, alpha),
          }
        : cur;
    const cs = this.predictedShip;
    const ps = this.prevPredictedShip;
    const c = this.shipCorrection;
    const ship =
      cs && ps && ps.id === cs.id
        ? {
            ...cs,
            position: { x: vLerp(ps.position, cs.position, alpha).x + c.x, y: vLerp(ps.position, cs.position, alpha).y + c.y },
            angle: lerpAngle(ps.angle, cs.angle, alpha) + c.angle,
          }
        : cs;
    return { me, ship };
  }

  // ------------------------------------------------------------- simulation

  private frame(now: number): void {
    const dt = Math.min(0.1, (now - this.lastFrame) / 1000);
    this.lastFrame = now;

    this.frameMs += (dt * 1000 - this.frameMs) * 0.05;
    const decay = Math.exp(-dt * 10);
    this.shipCorrection.x *= decay;
    this.shipCorrection.y *= decay;
    this.shipCorrection.angle *= decay;
    this.selfCorrection.x *= decay;
    this.selfCorrection.y *= decay;
    this.rideCorrection.x *= decay;
    this.rideCorrection.y *= decay;
    this.rockCorrection *= decay;

    this.accumulator += dt;
    while (this.accumulator >= INPUT_DT) {
      this.fixedStep(INPUT_DT);
      this.accumulator -= INPUT_DT;
    }
    if (this.photo) this.panPhoto(dt);

    const me = this.predicted;
    if (me?.frame) this.lastShipId = me.frame;
    // The touch buttons follow what we're doing.
    this.input.touch.setContext(me && me.gunning !== null ? "turret" : me?.piloting ? "helm" : "foot");
    if (me && this.sim) this.input.touch.setSituation(touchSituation(me, this.predSim()));
    if (me && this.sim) {
      // The world loads round us as we go, and we watch the areas within view.
      const at = playerWorldCenter(me, this.predSim());
      if (this.world?.update(at, this.rockTime, this.sim.knocks)) this.minimap.setAsteroids(this.sim.asteroids);
      this.links?.update(at, this.rockTime);
    }
    const self = this.blendedSelf(this.accumulator / INPUT_DT);
    if (this.audio.ambient(me, this.lastAxes, this.sim, dt)) this.hud.flashHurt();
    const others = this.interpolated(now);
    // Ships: our piloted ship from prediction, everything else from interpolated snapshots.
    const shipPose = others.shipPose;
    if (self.ship) shipPose.set(self.ship.id, self.ship);
    const players = others.players.filter((p) => p.id !== this.myPlayerId);
    if (self.me) players.push(self.me);
    const { cursor, note, sign } = this.building.update(dt);
    this.lastDrawnRockTime = this.drawnRockTime();

    this.renderer.update(dt, {
      ships: this.sim?.ships.values() ?? [],
      shipPose,
      players,
      myPlayerId: this.myPlayerId,
      projectiles: others.projectiles,
      drops: this.drops,
      cursor,
      cameraTurns: this.cameraTurns,
      modes: this.sim ? new Map(players.map((p) => [p.id, characterMode(p, this.predSim())])) : new Map(),
      creatures: this.withBands(this.drawnRockTime()),
      flares: this.flares,
      shutdown: this.shutdown,
      rifts: this.rifts,
      groundFires: this.groundFires,
      asteroids: this.asteroidById,
      // Drifting rocks drawn between input steps, as we are.
      rockAt: (def) => this.drawnRockOrigin(def),
      vinesCut: this.sim?.vinesCut ?? new Set(),
      photo: this.photo,
      photoPan: this.photoPan,
      path: this.pathAhead(me),
    });
    if (this.sim) this.drawMap(shipPose, players);
    // Walked away from the open chest (or it was dug up): put it away.
    const chest = this.crafting.chest?.target;
    if (this.sim && me && chest && !chestInReach(this.predSim(), chest, playerWorldCenter(me, this.predSim()))) this.crafting.close();
    if (this.sim && me && this.crafting.isOpen) {
      const atBench = !!tileNear(this.predSim(), playerWorldCenter(me, this.predSim()), T.WORKBENCH, CHARACTER.reach);
      this.crafting.update(this.building.inventory, atBench, this.building.onBar, this.building.gear);
    }
    if (this.sim && me) {
      this.updateIndicators(me, shipPose);
      this.audio.music(this.danger(me), dt);
    }

    // Stats for whichever ship you are aboard.
    const aboard = me?.frame ? this.sim?.ships.get(me.frame) : undefined;
    const flying = me?.piloting ? this.predictedShip : undefined;
    // The turret we're manning (on our ship, or an asteroid), if any.
    const gunHost = me && me.gunning !== null && this.sim ? turretHostOf(me, this.predSim()) : undefined;
    if (this.notice && now > this.notice.until) this.notice = null;
    // On touch, the status and hints name the touch buttons rather than keys.
    const wording = this.input.touch.enabled ? touchWording : (s: string) => s;
    this.hud.update({
      name: this.myName,
      mode: wording(statusText(me, this.sim && this.predSim(), flying, this.building.inventory)),
      hint: wording(note),
      speed: flying ? vLen(flying.velocity) : me ? vLen(me.velocity) : 0,
      // Manning a turret: whether it has reloaded.
      reloaded: !gunHost || (hostTurrets(gunHost)[me!.gunning!]?.cooldown ?? 0) <= 0,
      engines: aboard?.stats.engines ?? 0,
      turrets: aboard?.stats.turrets.length ?? 0,
      tiles: aboard?.stats.mass ?? 0,
      fuel: aboard?.stats.furnaces ? (flying ?? aboard.state).fuel / furnaceCapacity(aboard) : null,
      water: aboard?.stats.furnaces ? (flying ?? aboard.state).water / furnaceCapacity(aboard) : null,
      health: me ? me.health : 0,
      inventory: this.building.inventory,
      wear: this.building.wear,
      quickbar: this.building.quickbar,
      selected: this.building.selected,
      online: this.online,
      pingMs: this.pingMs,
      fps: 1000 / Math.max(1, this.frameMs),
      status: this.notice?.text ?? this.shutdownText() ?? this.status,
      readout: aboard ? readout(aboard) : "",
      pack: { used: slotsUsed(this.building.inventory), max: INVENTORY.slots },
      tooltip: this.signTip(sign),
      photo: this.photo,
    });

    requestAnimationFrame((t) => this.frame(t));
  }

  /**
   * The sphere's shutdown, as the status line has it (see NIGHT): the
   * countdown before it (worked out from the seed and the clock, like the
   * shutdown itself, so it costs no messages), and where it's got to once
   * it's under way. Null when there's nothing to say.
   */
  private shutdownText(): string | null {
    const seed = this.sim?.world.seed;
    if (seed === undefined || !this.rockTime) return null;
    if (this.shutdown !== null) {
      const o = outageState(this.shutdown);
      if (!o) return null;
      const min = Math.ceil(o.left / 60);
      return o.phase === "falter" ? "The stars are faltering: the sphere is shutting down" : o.phase === "dimming" ? `The stars are going out · light back in ${min} min` : o.phase === "out" ? `The stars are out · light back in ${min} min` : "The stars are coming back";
    }
    const left = shutdownWarning(seed, this.rockTime * 1000);
    if (left === null) return null;
    return left >= 90 ? `The stars go out in ${Math.round(left / 60)} min: get back to the stream, or to a light` : `The stars go out in ${Math.round(left)} s`;
  }

  /** Photo mode: WASD or the arrows pan the camera. */
  private panPhoto(dt: number): void {
    const speed = this.renderer.zoomLevel * 0.9 * dt;
    const held = (...codes: string[]) => codes.some((c) => this.input.codeHeld(c));
    this.photoPan.x += ((held("KeyD", "ArrowRight") ? 1 : 0) - (held("KeyA", "ArrowLeft") ? 1 : 0)) * speed;
    this.photoPan.y += ((held("KeyW", "ArrowUp") ? 1 : 0) - (held("KeyS", "ArrowDown") ? 1 : 0)) * speed;
  }

  /** Reading a sign by hovering over it: ask the server what it says (once), then show it by the mouse. */
  private signTip(sign: EditTarget | undefined): { text: string; x: number; y: number } | null {
    if (!sign) return null;
    const key = signKey(sign);
    if (!this.signsAsked.has(key)) {
      this.signsAsked.add(key);
      this.send({ t: "sign", target: sign });
    }
    const text = this.signs.get(key);
    return { text: text === undefined ? "..." : text || "(blank sign: right click to write on it)", x: this.input.mouseX, y: this.input.mouseY };
  }

  /** How dangerous things are right now, 0..1, for the music: pirates near, a flaring sun near, and low health. */
  private danger(me: PlayerState): number {
    const sim = this.predSim();
    const c = playerWorldCenter(me, sim);
    let d = Math.max(0, 1 - me.health / 60) * 0.8;
    for (const s of sim.ships.values()) if (s.state.pirate) d = Math.max(d, 1 - vDist(shipCenter(s), c) / DANGER_RANGE);
    for (const sun of sim.world.suns) if (this.flares.has(sun.id) && vDist(sun.position, c) < sun.radius + 600) d = Math.max(d, 0.7);
    for (const w of this.creatures) if (w.angry) d = Math.max(d, 0.6);
    // Night (see NIGHT): uneasy while your sun's out, more with something from outside close by.
    if (this.shutdown !== null) d = Math.max(d, 0.3);
    for (const w of this.creatures) if (isIncomer(w.kind) && vDist(w.position, c) < 60) d = Math.max(d, 0.55);
    return Math.min(1, d);
  }

  /** Arrows at the screen's edge pointing to pirates, other people, whales, and your own ship once you've stepped off it. */
  private updateIndicators(me: PlayerState, shipPose: Map<string, ShipPose>): void {
    const sim = this.predSim();
    const c = playerWorldCenter(me, sim);
    const items: Indicator[] = [];
    const add = (p: Vec2, kind: Indicator["kind"], label?: string) => items.push({ ...this.renderer.worldToScreen(p), kind, label });
    const centreOf = (s: ShipBody) => {
      const pose = shipPose.get(s.state.id) ?? s.state;
      return { x: pose.position.x + s.grid.w / 2, y: pose.position.y + s.grid.h / 2 };
    };
    if (!this.photo) {
      for (const s of sim.ships.values()) {
        if (s.state.pirate && vDist(shipCenter(s), c) < NET.viewRadius) add(centreOf(s), "pirate", "pirates");
      }
      const mine = this.lastShipId ? sim.ships.get(this.lastShipId) : undefined;
      if (mine && me.frame !== mine.state.id) add(centreOf(mine), "ship", "your ship");
      for (const p of this.players.values()) if (p.id !== me.id) add(playerWorldCenter(p, sim), "crew", p.name);
      for (const w of this.creatures) if (w.kind === "whale") add(w.position, "whale", w.angry ? "angry whale" : "whale");
    }
    this.indicators.update(items);
  }

  /**
   * The sound of tile `was` becoming `now` at `at`: digging (it's gone, or a
   * piece came off leaving the back wall behind it) sounds like what was dug,
   * anything else like what went in.
   */
  private blockSound(was: number, now: number, at: Vec2): void {
    const dug = now === T.EMPTY || (!!pieceOf(was) && !pieceOf(now));
    this.audio.block(dug, dug ? was : now, at);
  }

  /** Someone else built at `at`: the nearest player within reach swings. (Digging shows as they swing, from `using`.) */
  private swingNear(at: Vec2, tile: number): void {
    if (!this.sim || tile === T.EMPTY) return;
    let best: PlayerState | undefined;
    let bestD = CHARACTER.reach + 1.5;
    for (const p of this.players.values()) {
      const d = vDist(playerWorldCenter(p, this.sim), at);
      if (d < bestD) [best, bestD] = [p, d];
    }
    // Our own builds already swung when we clicked.
    if (best && best.id !== this.myPlayerId) this.renderer.swing(best.id);
  }

  /** Ships and characters on the minimap, where they are being drawn this frame. */
  private drawMap(shipPose: Map<string, ShipPose>, players: PlayerState[]): void {
    const sim = this.predSim();
    const me = players.find((p) => p.id === this.myPlayerId);
    const ships: MapShip[] = [];
    for (const body of sim.ships.values()) {
      const pose = shipPose.get(body.state.id) ?? body.state;
      // Most of a ship's grid is empty; draw it about as big as its tiles reach.
      const r = Math.max(4, body.stats.radius);
      ships.push({
        center: { x: pose.position.x + body.grid.w / 2, y: pose.position.y + body.grid.h / 2 },
        angle: pose.angle,
        w: r * 1.6,
        h: r * 0.7,
        aboard: body.state.id === me?.frame,
        drifting: body.stats.helms === 0,
        pirate: !!body.state.pirate,
      });
    }
    this.minimap.setSunOutput(this.shutdown === null ? 1 : (outageState(this.shutdown)?.output ?? 1));
    this.minimap.draw({
      me: me && playerWorldCenter(me, sim),
      ships,
      others: players.filter((p) => p !== me).map((p) => playerWorldCenter(p, sim)),
      creatures: this.creatures.filter((c) => c.kind === "whale").map((c) => c.position),
    });
  }

  /** The dotted line (see PathLine): where a push off will take us as we brace for it, and nothing at any other time. */
  private pathAhead(me: PlayerState | null | undefined): PathView | null {
    if (!me || !this.sim || me.frame || me.piloting || me.gunning !== null || me.vine || me.grapple || this.photo) return null;
    // Only while bracing: nothing is drawn while you drift, where it was on the whole time you were off the rock.
    const charge = me.leap ?? 0;
    if (charge <= 0) return null;
    const sim = this.predSim();
    const from = playerWorldCenter(me, sim);
    // Where the push off would take us: always away from the rock (see leapDirection).
    const dir = leapDirection(me, from, upVector(surfaceUp(sim, from) ?? me.up));
    const reach = Math.min(PATH_REACH, (LEAP.minSpeed + (LEAP.maxSpeed - LEAP.minSpeed) * charge) / CHARACTER.airDrag);
    // Coarse steps: this line is hundreds of blocks long, and a block either way on where it ends doesn't show.
    const hit = grappleHit(sim, from, vAdd(from, dir), reach, false, undefined, 1.5);
    const to = hit && hookPoint(hit.at, hit.rock, sim);
    return { from, to: to ?? vAdd(from, vScale(dir, reach)), hit: !!to };
  }

  private fixedStep(dt: number): void {
    if (!this.predicted || !this.sim || !this.links?.connected) return;

    // On touch with no finger on the world, we face the way the stick points (we're near the middle of the screen).
    const hint = this.input.aimHint();
    const mouse = hint
      ? this.renderer.screenToWorld(screenW() / 2 + hint.x * 120, screenH() / 2 - hint.y * 120)
      : this.renderer.screenToWorld(this.input.mouseX, this.input.mouseY);
    const aim = this.predicted.gunning !== null ? this.turretAim(mouse) : mouse;
    const axes = this.input.sample(!!this.predicted.piloting);
    this.lastAxes = axes;
    const input: PlayerInput = {
      seq: ++this.seq,
      dt,
      ...axes,
      fire: this.input.leftDown && !this.photo,
      hold: this.building.heldItem(),
      aimX: aim.x,
      aimY: aim.y,
    };
    this.prevPredicted = structuredClone(this.predicted);
    this.prevRide = this.rideOf(this.predicted);
    this.prevPredictedShip = this.predictedShip && structuredClone(this.predictedShip);
    this.predictStep(input);

    // Standing still with nothing pressed: nothing to send (and nothing for the server to replay).
    if (this.idleStep(input)) {
      this.flushInputs(true);
      return;
    }
    // A press or a click goes at once; walking and aiming wait for a full batch.
    const urgent = input.interact || !!input.action || input.fire !== !!this.lastSent?.fire;
    this.lastSent = input;
    this.pending.push(input);
    if (this.pending.length > MAX_PENDING) this.pending.shift();
    this.outBatch.push(input);
    this.flushInputs(urgent);
  }

  /**
   * Turret mouse sensitivity: the gun's swing away from straight up is the
   * mouse's, times the setting, so a low setting gives fine control.
   */
  private turretAim(mouse: Vec2): Vec2 {
    const me = this.predicted!;
    const c = playerWorldCenter(me, this.predSim());
    const k = this.settings.turretSensitivity;
    const dx = mouse.x - c.x;
    const dy = mouse.y - c.y;
    const r = Math.hypot(dx, dy);
    const up = Math.PI / 2 + (this.predSim().ships.get(me.frame ?? "")?.state.angle ?? 0);
    const a = up + wrapAngle(Math.atan2(dy, dx) - up) * k;
    return { x: c.x + Math.cos(a) * r, y: c.y + Math.sin(a) * r };
  }
}

/** The ship readout under its stats: how hard its engines push each way, and how its weight sits. */
function readout(ship: ShipBody): string {
  if (!ship.stats.engines) return "No steam engines: it can't fly";
  const r = flightReadout(ship);
  const f = (n: number) => n.toFixed(1);
  const off = Math.hypot(r.offset.x, r.offset.y);
  const balance =
    off < 1.5 ? "balanced" : `weight ${f(off)} tiles ${Math.abs(r.offset.x) > Math.abs(r.offset.y) ? (r.offset.x > 0 ? "forward" : "aft") : r.offset.y > 0 ? "high" : "low"}`;
  return `Thrust m/s²: lift ${f(r.lift)} · sink ${f(r.sink)} · fwd ${f(r.forward)} · back ${f(r.back)} · turn ${f(r.turn)} · ${balance}`;
}

const signKey = (t: EditTarget) => `${t.kind}:${t.id}:${t.x}:${t.y}`;

/** Whether the chest (or crate) at `target` is still there, and within reach (with a little slack) of `from`. */
function chestInReach(sim: Sim, target: EditTarget, from: Vec2): boolean {
  if (!isContainer(editTargetTile(sim, target))) return false;
  const at = editTargetCenter(sim, target);
  return !!at && vDist(at, from) <= CHARACTER.useReach + 1;
}
